import { styled } from '@mui/material/styles';
import {
  Box,
  Paper,
  Button,
  TextField,
  Typography,
  Drawer,
  Chip
} from '@mui/material';
import { FORD_COLORS, FORD_RADIUS, FORD_SPACING, FORD_TYPOGRAPHY } from '../../theme/fordTheme.js';

// Common style constants
const COMMON_STYLES = {
  border: '1px solid #233058',
  borderRadius: '8px',
  borderRadiusLarge: '12px',
  borderRadiusSmall: '6px',
  padding: '16px',
  paddingLarge: '20px',
  paddingSmall: '12px',
  fontSize: '0.875rem',
  fontSizeSmall: '0.75rem',
  transition: 'all 0.2s ease',
  hoverBackground: 'rgba(79, 124, 255, 0.1)',
  hoverBorderColor: '#4f7cff',
};

// Common chip color configurations
const CHIP_COLORS = {
  'chip-ok': {
    backgroundColor: 'rgba(34, 197, 94, 0.2)',
    color: '#22c55e',
    border: '1px solid rgba(34, 197, 94, 0.3)',
  },
  'chip-warn': {
    backgroundColor: 'rgba(251, 191, 36, 0.2)',
    color: '#fbbf24',
    border: '1px solid rgba(251, 191, 36, 0.3)',
  },
  'chip-error': {
    backgroundColor: 'rgba(239, 68, 68, 0.2)',
    color: '#ef4444',
    border: '1px solid rgba(239, 68, 68, 0.3)',
  },
  default: {
    backgroundColor: 'rgba(156, 163, 199, 0.2)',
    color: '#9aa3c7',
    border: '1px solid rgba(156, 163, 199, 0.3)',
  },
};


// Main drawer component
export const PlugDrawerContainer = styled(Drawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    width: '675px',
    maxWidth: '90vw',
    backgroundColor: FORD_COLORS.backgroundford,
    color: FORD_COLORS.textMuted,
    border: COMMON_STYLES.border,
    borderRadius: COMMON_STYLES.borderRadiusSmall,
  },
}));

// Drawer header
export const PlugDrawerHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start', // Changed to align text and button properly
  padding: '20px 24px', // Increased padding for better spacing
  backgroundColor: 'rgb(10, 37, 64)', // Dark navy blue background matching OfflineEventsDrawer
  color: '#ffffff',
  borderBottom: 'none', // Remove border for clean look
  minHeight: '80px', // Ensure adequate height for two-line layout
  '& h3': {
    margin: 0,
    fontSize: '1.25rem',
    fontWeight: 600,
    color: '#ffffff',
  },
  '& .MuiButton-root': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)', // Semi-transparent button background
    color: '#ffffff',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    borderRadius: '6px',
    padding: '8px 16px',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.2)',
    },
  },
  '& .MuiIconButton-root': {
    color: '#ffffff',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
  },
}));

// Drawer header title styling
export const PlugDrawerTitle = styled(Typography)(({ theme }) => ({
  fontSize: '12px',
  fontWeight: 500,
  color: 'rgba(255, 255, 255, 0.7)',
  textTransform: 'uppercase',
  letterSpacing: '1px',
  lineHeight: 1.2,
  display: 'block'
}));

// Drawer header device name styling
export const PlugDrawerDeviceName = styled(Typography)(({ theme }) => ({
  fontSize: '24px',
  fontWeight: 700,
  color: '#ffffff',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  lineHeight: 1.2,
  marginTop: '4px'
}));

// Drawer content area
export const PlugDrawerContent = styled(Box)(({ theme }) => ({
  padding: COMMON_STYLES.paddingLarge,
  height: '100%',
  overflowY: 'auto',
  backgroundColor: FORD_COLORS.backgroundford,
}));

// Meta information section
export const PlugDrawerMetaSection = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 2fr))', // Increased minWidth for better spacing
  padding: COMMON_STYLES.padding,
  borderRadius: COMMON_STYLES.borderRadiusLarge,
  marginBottom: COMMON_STYLES.paddingLarge,
  border: 'none',
  boxShadow: '0 4px 12px rgba(0,0,0,0.15), 0 2px 4px rgba(0,0,0,0.1)',
  backgroundColor: '#ffffff', // Ensure white background
  '& > div': {
    display: 'flex',
    flexDirection: 'column',
    // gap: '1px',
    paddingTop: '9px', // Add some vertical padding
    '& b': {
      textTransform: 'uppercase',
      fontWeight: 700,
      color: 'rgb(138, 152, 166)',
      fontSize: '12px',
      letterSpacing: '0.5px',
      lineHeight: 1.2,
      marginBottom: '2px',
    },
    '& span': {
      color: 'rgb(19, 34, 52)',
      fontSize: '14px',
      fontWeight: 500,
      lineHeight: 1.3,
    },
  },
}));

// Date filters panel
export const PlugDrawerDateFiltersPanel = styled(Paper)(({ theme }) => ({
  backgroundColor: FORD_COLORS.backgroundford,
  borderRadius: '12px',
  padding: '16px',
  marginBottom: '20px',
  border: 'none',
  boxShadow: '0 4px 12px rgba(0,0,0,0.15), 0 2px 4px rgba(0,0,0,0.1)',
}));

// Date filters container
export const PlugDrawerDateFiltersContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '4px',
  alignItems: 'flex-start',
  flexWrap: 'wrap',
  marginBottom: '20px',
  '& .date-field-container': {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    minWidth: '200px',
  },
  '& .date-label': {
    textTransform: 'uppercase',
    fontWeight: 700,
    color: 'rgb(138, 152, 166)',
    fontSize: '12px',
    letterSpacing: '0.5px',
    lineHeight: 1.2,
    marginBottom: '4px',
  },
}));

// Left section for date inputs and download button
export const PlugDrawerDateFiltersLeft = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '2px',
  alignItems: 'center',
  flexWrap: 'wrap',
}));

// Right section for range display
export const PlugDrawerDateFiltersRight = styled(Box)(({ theme }) => ({
  display: 'flex',
  // gap: '12px',
  alignItems: 'center',
}));

// Date input field styling
export const PlugDrawerDateField = styled(TextField)(({ theme }) => ({
  minWidth: '200px',
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#ffffff',
    color: 'rgb(19, 34, 52)',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: 500,
    '& fieldset': {
      borderColor: 'rgba(138, 152, 166, 0.3)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(138, 152, 166, 0.6)',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#4f7cff',
      borderWidth: '2px',
    },
  },
  '& .MuiInputLabel-root': {
    display: 'none', // Hide default label since we're using custom labels
  },
  '& .MuiOutlinedInput-input': {
    color: 'rgb(19, 34, 52)',
    fontSize: '14px',
    fontWeight: 500,
    padding: '12px 14px',
    '&::-webkit-calendar-picker-indicator': {
      filter: 'invert(0)', // Black color
      cursor: 'pointer',
      opacity: 1,
      width: '20px',
      height: '20px',
      backgroundColor: 'transparent',
    },
    // For Firefox
    '&::-moz-calendar-picker-indicator': {
      filter: 'invert(0)', // Black color
      cursor: 'pointer',
      opacity: 1,
    },
  },
}));

// Date field label component
export const PlugDrawerDateFieldLabel = styled(Typography)(({ theme }) => ({
  textTransform: 'uppercase',
  fontWeight: 700,
  color: 'rgb(138, 152, 166)',
  fontSize: '12px',
  letterSpacing: '0.5px',
  lineHeight: 1.2,
}));

// Range display text
export const PlugDrawerRangeText = styled(Typography)(({ theme }) => ({
  color: FORD_COLORS.textMuted,
  fontSize: '14px',
}));

// Advanced Search Toggle Button
export const PlugDrawerAdvancedSearchToggle = styled(Button)(({ theme }) => ({
  color: FORD_COLORS.secondary,
  backgroundColor: 'transparent',
  border: 'none !important',
  fontSize: '14px',
  fontWeight: '500',
  textTransform: 'none',
  padding: '0',
  minWidth: 'auto',
  justifyContent: 'flex-start',
  boxShadow: 'none !important',
  borderRadius: '0',
  '& .MuiButton-startIcon': {
    marginRight: '4px',
    marginLeft: '0',
  },
  '&:hover': {
    backgroundColor: 'transparent',
    color: FORD_COLORS.primaryLight,
    border: 'none !important',
    boxShadow: 'none !important',
  },
  '&:focus': {
    backgroundColor: 'transparent',
    border: 'none !important',
    boxShadow: 'none !important',
    outline: 'none',
  },
  '&:active': {
    backgroundColor: 'transparent',
    border: 'none !important',
    boxShadow: 'none !important',
  },
  '&.Mui-focusVisible': {
    backgroundColor: 'transparent',
    border: 'none !important',
    boxShadow: 'none !important',
    outline: 'none',
  },
}));

// Advanced Search Container
export const PlugDrawerAdvancedSearchContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: '10px',
  marginTop: '10px',
}));

// Primary button styling
export const PlugDrawerPrimaryButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#066fef',
  color: '#ffffff',
  textTransform: 'none',
  borderRadius: COMMON_STYLES.borderRadius,
  padding: '8px 16px',
  fontWeight: '500',
  transition: COMMON_STYLES.transition,
  '&:hover': {
    backgroundColor: '#0554c7',
  },
  '&:disabled': {
    backgroundColor: '#233058',
    color: '#9aa3c7',
  },
}));

// Secondary button styling - for close buttons, etc.
export const PlugDrawerSecondaryButton = styled(Button)(({ theme }) => ({
  backgroundColor: 'transparent',
  color: FORD_COLORS.textLight,
  border: COMMON_STYLES.border,
  borderRadius: COMMON_STYLES.borderRadiusSmall,
  padding: '8px 16px',
  textTransform: 'none',
  fontWeight: 500,
  transition: COMMON_STYLES.transition,
  '&:hover': {
    backgroundColor: COMMON_STYLES.hoverBackground,
    borderColor: COMMON_STYLES.hoverBorderColor,
    transform: 'translateY(-1px)',
    color: FORD_COLORS.textLight,
  },
  '&:disabled': {
    backgroundColor: 'rgba(30,45,90,0.1)',
    color: '#6b7280',
    borderColor: '#374151',
    cursor: 'not-allowed',
    transform: 'none',
  },
}));

// Tab navigation container
export const PlugDrawerTabNavigation = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '12px',
  marginBottom: '20px',
  borderBottom: '1px solid #233058',
  paddingBottom: '12px',
  justifyContent: 'space-between'
}));

// Tab button (active state handled via props)
export const PlugDrawerTabButton = styled(Button)(({ theme, active }) => ({
  // backgroundColor: active ? '#4f7cff' : 'transparent',
  // backgroundColor: `${FORD_COLORS.primaryLight}20`,
  // color: active ? FORD_COLORS.textMuted : '#9aa3c7',
  // border: '1px solid #233058',
  // borderRadius: '8px',
  // padding: '8px 16px',
  // textTransform: 'none',
  // fontWeight: 500,
  // '&:hover': {
  //   backgroundColor: active ? '#4f7cff' : 'rgba(79, 124, 255, 0.1)',
  //   color: active ? FORD_COLORS.textMuted : '#e7ecff',
  // },

  textTransform: 'none',
  padding: `${FORD_SPACING.sm} ${FORD_SPACING.md}`,
  borderRadius: FORD_RADIUS.sm,
  // color: FORD_COLORS.textLight,
  border: '1px solid #233058',
  // fontSize: FORD_TYPOGRAPHY.body2,
  // fontWeight: FORD_TYPOGRAPHY.medium,
  // fontFamily: FORD_TYPOGRAPHY.fallbackFont,
  transition: 'all 0.2s ease-in-out',
  display: 'inline-block',
  whiteSpace: 'nowrap',
  backgroundColor: active ? '#4f7cff' : 'rgba(79, 124, 255, 0.1)',
  color: active ? FORD_COLORS.textColorWhite : FORD_COLORS.textMuted,
  '&:hover': {
    backgroundColor: `${FORD_COLORS.primaryLight}20`,
    transform: 'translateY(-1px)',
  },
  '&.active': {
    backgroundColor: FORD_COLORS.secondary,
    // boxShadow: FORD_SHADOWS.sm,
    // color: FORD_COLORS.textColorWhite,
  },
}));

// Content panel for tabs
export const PlugDrawerTabContentPanel = styled(Box)(({ theme }) => ({
  backgroundColor: FORD_COLORS.backgroundford,
  border: 'none',
  borderRadius: COMMON_STYLES.borderRadius,
  boxShadow: '0 4px 12px rgba(0,0,0,0.15), 0 2px 4px rgba(0,0,0,0.1)',
  padding: '16px',
}));

// Tab content header
export const PlugDrawerTabContentHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '6px',
  '& h4': {
    color: FORD_COLORS.textMuted,
    margin: 0,
    fontSize: '1.125rem',
    fontWeight: 600,
  },
}));

// Table wrapper
export const PlugDrawerTableWrapper = styled(Box)(({ theme }) => ({
  overflowX: 'auto',
  overflowY: 'auto',
  position: 'relative',
  border: COMMON_STYLES.border,
  // borderRadius: COMMON_STYLES.borderRadius,
  // boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
  // marginBottom: COMMON_STYLES.paddingLarge,
  // Hide scrollbar while maintaining scroll functionality
  scrollbarWidth: 'none', // Firefox
  '&::-webkit-scrollbar': {
    display: 'none', // Chrome, Safari, Edge
  },
  msOverflowStyle: 'none', // Internet Explorer 10+
  '& table': {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: 0,
    fontSize: '12px',
    '& thead': {
      position: 'sticky',
      top: 0,
      zIndex: 10,
    },
    '& th': {
      backgroundColor: 'rgb(10, 37, 64)', // Dark navy blue background to match OfflineEventsDrawer
      color: '#ffffff', // White text
      fontWeight: 700, // Bolder font weight
      textTransform: 'uppercase', // Capital letters
      letterSpacing: '0.5px', // Better readability
      padding: `${COMMON_STYLES.paddingSmall} ${COMMON_STYLES.padding}`,
      textAlign: 'left',
      borderBottom: 'none', // Remove border to match OfflineEventsDrawer
      position: 'sticky',
      top: 0,
      zIndex: 11,
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
      borderRadius: 0,
      // Column-specific widths for alerts table
      '&:nth-of-type(1)': { width: '25%' }, // Device/Alert Type
      '&:nth-of-type(2)': { width: '25%' }, // Severity/Status
      '&:nth-of-type(3)': { width: '30%' }, // Time
      '&:nth-of-type(4)': { width: '20%' }, // Additional Info
    },
    '& tbody': {
      position: 'relative',
      zIndex: 1,
    },
    '& td': {
      padding: `8px`,
      borderBottom: '1px solid rgba(35, 48, 88, 0.5)',
      color: 'rgb(19, 34, 52)',
      backgroundColor: FORD_COLORS.backgroundford,
      // Match header column widths
      '&:nth-of-type(1)': { width: '25%' },
      '&:nth-of-type(2)': { width: '25%' },
      '&:nth-of-type(3)': { width: '30%' },
      '&:nth-of-type(4)': { width: '20%' },
    },
    '& tr:hover td': {
      backgroundColor: COMMON_STYLES.hoverBackground,
    },
  },
}));

// Status chip styling
export const PlugDrawerStatusChip = styled(Chip)(({ theme, chiptype }) => {
  return {
    ...(CHIP_COLORS[chiptype] || CHIP_COLORS.default),
    fontSize: COMMON_STYLES.fontSizeSmall,
    height: '24px',
    borderRadius: COMMON_STYLES.borderRadiusLarge,
    fontWeight: '500',
  };
});

// Socket chip
export const PlugDrawerSocketChip = styled(Chip)(({ theme }) => ({
  backgroundColor: 'rgba(79, 124, 255, 0.2)',
  color: '#4f7cff',
  border: '1px solid rgba(79, 124, 255, 0.3)',
  fontSize: '12px',
  height: '24px',
  fontWeight: 500,
}));

// Loading message
export const PlugDrawerLoadingMessage = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: '40px',
  fontSize: '1rem',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '16px',
  color: FORD_COLORS.textMuted,
}));

// Spinner
export const PlugDrawerSpinner = styled(Box)(({ theme }) => ({
  width: '20px',
  height: '20px',
  border: '2px solid #233058',
  borderTop: '2px solid #4f7cff',
  borderRadius: '50%',
  animation: 'spin 1s linear infinite',
  '@keyframes spin': {
    '0%': { transform: 'rotate(0deg)' },
    '100%': { transform: 'rotate(360deg)' },
  },
}));

// Error box  
export const PlugDrawerErrorBox = styled(Box)(({ theme }) => ({
  backgroundColor: 'rgba(239, 68, 68, 0.1)',
  border: '1px solid rgba(239, 68, 68, 0.3)',
  borderRadius: COMMON_STYLES.borderRadiusLarge,
  padding: COMMON_STYLES.padding,
  marginBottom: COMMON_STYLES.paddingLarge,
}));

// Error header
export const PlugDrawerErrorHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: '12px',
  '& .error-icon': {
    fontSize: '20px',
    marginRight: '8px',
  },
  '& .error-title': {
    color: '#ef4444',
    fontWeight: 600,
  },
}));

// Error message text
export const PlugDrawerErrorMessage = styled(Typography)(({ theme }) => ({
  color: '#ef4444',
  marginBottom: '12px',
}));

// Error help text
export const PlugDrawerErrorHelp = styled(Typography)(({ theme }) => ({
  color: FORD_COLORS.textMuted,
  fontSize: '0.875rem',
}));

// Pagination container
export const PlugDrawerPagination = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '10px 0px',
  gap: '10px',
  borderTop: COMMON_STYLES.border,
  marginTop: '10px',
  '& button': {
    backgroundColor: 'transparent',
    border: COMMON_STYLES.border,
    borderRadius: COMMON_STYLES.borderRadiusSmall,
    padding: '6px 12px',
    fontSize: COMMON_STYLES.fontSize,
    cursor: 'pointer',
    transition: COMMON_STYLES.transition,
    color: FORD_COLORS.textMuted,
    minWidth: '36px',
    '&:hover': {
      backgroundColor: COMMON_STYLES.hoverBackground,
      borderColor: COMMON_STYLES.hoverBorderColor,
    },
    '&:disabled': {
      backgroundColor: 'transparent',
      color: '#9aa3c7',
      borderColor: '#233058',
      opacity: 0.5,
      cursor: 'not-allowed',
    },
    '&.active': {
      backgroundColor: '#4f7cff',
      color: '#ffffff',
      borderColor: '#4f7cff',
      '&:hover': {
        backgroundColor: '#3d63cc',
      },
    },
  },
}));

// Pagination text
export const PlugDrawerPaginationText = styled(Typography)(({ theme }) => ({
  color: FORD_COLORS.textMuted,
  fontSize: COMMON_STYLES.fontSize,
}));

// Empty state
export const PlugDrawerEmptyState = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: '60px 20px',
  color: FORD_COLORS.textMuted,
  '& .MuiTypography-h6': {
    color: FORD_COLORS.textLight,
    marginBottom: '8px',
  },
  '& .MuiTypography-body2': {
    color: FORD_COLORS.textMuted,
  },
}));

// Device detail row container for metadata fields
export const PlugDeviceDetailRowContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  padding: '1px 0', // Add some vertical padding
  fontFamily: FORD_TYPOGRAPHY.newfont,
  '& .detail-label': {
    textTransform: 'uppercase',
    fontWeight: 700,
    color: 'rgb(138, 152, 166)',
    fontSize: '12px',
    letterSpacing: '0.5px',
    lineHeight: 1.2,
    marginBottom: '2px',
  },
  '& .detail-value': {
    color: 'rgb(19, 34, 52)',
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: 1.3,
  },
}));

// Critical device display with tooltip
export const PlugDrawerCriticalDeviceDisplay = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  '& .info-icon': {
    fontSize: '16px',
    color: '#4f7cff',
  },
  '& .copy-icon': {
    fontSize: '14px',
    color: '#9aa3c7',
    cursor: 'pointer',
    '&:hover': {
      color: '#4f7cff',
    }
  }
}));
